import { StorageAdapter } from "./StorageAdapter.ts";

/**
 * Probe whether `localStorage` is actually usable in this context.
 *
 * BL-85: it is not enough to feature-detect the property — in a sandboxed
 * iframe or with site-data blocked, the *property access itself* throws a
 * `SecurityError`, and in private mode `setItem` can throw a quota error
 * even though the object exists. We therefore guard the access AND do a
 * real set/remove of a sentinel inside the try/catch.
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return false;
    const probe = "__aiw_probe__";
    ls.setItem(probe, "1");
    ls.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/**
 * `localStorage`-backed {@link StorageAdapter}, hardened to degrade rather
 * than throw (BL-85). Every method wraps the raw `globalThis.localStorage`
 * access — which can throw on the property access alone — in try/catch:
 * reads degrade to `null`/`[]`, writes degrade to a logged no-op. A throw
 * here can therefore never abort boot or the running game.
 *
 * When storage is unreachable from the start, prefer selecting
 * {@link MemoryStorageAdapter} via {@link createStorageAdapter} so saves at
 * least round-trip within the session; this class's per-method degrade is
 * the belt-and-braces net for storage that fails mid-session.
 */
export class LocalStorageAdapter implements StorageAdapter {
  private prefix: string;
  private warned = false;

  constructor(prefix: string = "aiw_") {
    this.prefix = prefix;
  }

  private warnOnce(op: string, e: unknown): void {
    if (this.warned) return;
    this.warned = true;
    console.warn(
      `[LocalStorageAdapter] storage unavailable (${op}) — degrading to no-op:`,
      e,
    );
  }

  async save(key: string, data: string): Promise<void> {
    try {
      globalThis.localStorage.setItem(this.prefix + key, data);
    } catch (e: unknown) {
      // Quota, SecurityError, or a throwing property access: degrade to a
      // logged no-op so a write can never abort boot or the game loop.
      this.warnOnce("save", e);
    }
  }

  async load(key: string): Promise<string | null> {
    try {
      return globalThis.localStorage.getItem(this.prefix + key);
    } catch (e: unknown) {
      this.warnOnce("load", e);
      return null;
    }
  }

  async remove(key: string): Promise<void> {
    try {
      globalThis.localStorage.removeItem(this.prefix + key);
    } catch (e: unknown) {
      this.warnOnce("remove", e);
    }
  }

  async listKeys(prefix: string): Promise<string[]> {
    try {
      const keys: string[] = [];
      const fullPrefix = this.prefix + prefix;
      for (let i = 0; i < globalThis.localStorage.length; i++) {
        const key = globalThis.localStorage.key(i);
        if (key?.startsWith(fullPrefix)) {
          keys.push(key.slice(this.prefix.length));
        }
      }
      return keys;
    } catch (e: unknown) {
      this.warnOnce("listKeys", e);
      return [];
    }
  }
}
